import React, { useState, useEffect } from "react";
import Modal from "../Modal";
import { getReceipt, getPharmacyBillHtml, getOpdBillHtml } from "../../apis/receipts";
import { getInvoiceBill } from "../../apis/completedPayments";
import OpdReceipt from "./OpdReceipt";
import PathologyReceipt from "./PathologyReceipt";
import IpdReceipt from "./IpdReceipt";
import PharmacyBill from "./PharmacyBill";
import ReceiptSlipModal, { ReceiptSlipData } from "./ReceiptSlipModal";
import TransactionsModal from "./TransactionsModal";

type ReceiptType = "opd" | "ipd" | "pathology" | "pharmacy";

type Transaction = {
  allocation_id: number;
  transaction_id: number;
  payment_date: string | null;
  payment_mode: string;
  amount: number;
  receipt_number: string | null;
};

type ReceiptModalProps = {
  isOpen: boolean;
  onClose: () => void;
  invoiceId: number | null;
  receiptType: ReceiptType;
};

const ReceiptModal: React.FC<ReceiptModalProps> = ({
  isOpen,
  onClose,
  invoiceId,
  receiptType,
}) => {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [invoice, setInvoice] = useState<any>(null);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [transactionsLoading, setTransactionsLoading] = useState(false);
  const [showTransactionsModal, setShowTransactionsModal] = useState(false);
  const [selectedReceiptData, setSelectedReceiptData] = useState<ReceiptSlipData | null>(null);

  useEffect(() => {
    if (!isOpen || !invoiceId) {
      setData(null);
      setError(null);
      setInvoice(null);
      setTransactions([]);
      setShowTransactionsModal(false);
      setSelectedReceiptData(null);
      return;
    }

    setLoading(true);
    setError(null);
    getReceipt({ invoiceId, type: receiptType })
      .then((res: any) => {
        const result = res?.data ?? res;
        setData(result);
      })
      .catch((err: any) => {
        const msg =
          err?.response?.data?.error ??
          err?.response?.data?.message ??
          err?.message ??
          "Failed to load receipt";
        setError(msg);
      })
      .finally(() => setLoading(false));

    setTransactionsLoading(true);
    getInvoiceBill(invoiceId)
      .then((res: any) => {
        const result = res?.data ?? res;
        setInvoice(result?.invoice ?? null);
        setTransactions(result?.transactions ?? []);
      })
      .catch(() => setTransactions([]))
      .finally(() => setTransactionsLoading(false));
  }, [isOpen, invoiceId, receiptType]);

  const handlePrint = () => {
    if (receiptType === "opd" || receiptType === "pharmacy") {
      if (!invoiceId) return;
      setPdfLoading(true);
      const htmlApi = receiptType === "opd"
        ? () => getOpdBillHtml({ invoiceId: invoiceId! })
        : () => getPharmacyBillHtml({ invoiceId: invoiceId! });
      htmlApi()
        .then((res: any) => {
          const blobUrl = URL.createObjectURL(new Blob([res.data], { type: "text/html" }));
          window.open(blobUrl, "_blank");
          setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
        })
        .catch((err: any) => {
          const msg =
            err?.response?.data?.error ??
            err?.response?.data?.message ??
            err?.message ??
            "Failed to generate bill";
          setError(msg);
        })
        .finally(() => setPdfLoading(false));
    } else {
      window.print();
    }
  };

  const isPharmacy = receiptType === "pharmacy";

  const hasSingleTransaction = transactions.length === 1;
  const hasMultipleTransactions = transactions.length > 1;

  if (selectedReceiptData) {
    return (
      <ReceiptSlipModal
        isOpen={true}
        onClose={() => setSelectedReceiptData(null)}
        receiptData={selectedReceiptData}
      />
    );
  }

  if (showTransactionsModal && invoiceId) {
    return (
      <TransactionsModal
        isOpen={true}
        onClose={() => setShowTransactionsModal(false)}
        invoiceId={invoiceId}
      />
    );
  }

  return (
    <Modal
      title="View Bill"
      onClose={onClose}
      width={isPharmacy ? "sm:max-w-6xl" : "sm:max-w-4xl"}
      modalContent={
        <div className="receipt-modal-content">
          {loading && (
            <div className="py-12 text-center text-gray-500">
              Loading receipt...
            </div>
          )}
          {error && (
            <div className="py-8 text-center text-red-600">{error}</div>
          )}
          {isPharmacy && !error && data && (
            <>
              <div className="p-4 bg-white receipt-print-area pharmacy-bill-print-area">
                <PharmacyBill data={data} />
              </div>
              <div className="flex flex-wrap gap-2 justify-end mt-4 print:hidden">
                {!transactionsLoading && hasSingleTransaction && invoice && (
                  <button
                    type="button"
                    onClick={() => {
                      const tx = transactions[0];
                      setSelectedReceiptData({
                        patientName: invoice.patientName ?? "",
                        receiptNumber: (tx as any).receiptNumber ?? (tx as any).receipt_number ?? "",
                        invoiceNumber: invoice.invoiceNumber ?? "",
                        paymentDate: (tx as any).paymentDate ?? (tx as any).payment_date,
                        paymentMode: (tx as any).paymentMode ?? (tx as any).payment_mode ?? "",
                        amount: tx.amount,
                        clinicName: invoice.clinicName,
                        notes: (tx as any).notes,
                      });
                    }}
                    className="px-4 py-2 text-sm font-semibold rounded-md border text-primary border-primary hover:bg-primary/5"
                  >
                    View Receipt
                  </button>
                )}
                {!transactionsLoading && hasMultipleTransactions && (
                  <button
                    type="button"
                    onClick={() => setShowTransactionsModal(true)}
                    className="px-4 py-2 text-sm font-semibold rounded-md border text-primary border-primary hover:bg-primary/5"
                  >
                    View Transactions
                  </button>
                )}
                <button
                  type="button"
                  onClick={handlePrint}
                  disabled={!invoiceId || pdfLoading}
                  className="px-4 py-2 text-sm font-semibold text-white rounded-md bg-primary hover:bg-hover disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {pdfLoading ? "Generating..." : "Print"}
                </button>
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 text-sm font-semibold text-gray-700 rounded-md border border-gray-300 hover:bg-gray-50"
                >
                  Close
                </button>
              </div>
            </>
          )}
          {!loading && !error && data && !isPharmacy && (
            <>
              <div className="p-4 bg-white receipt-print-area">
                {data.receiptType === "opd" && <OpdReceipt data={data} />}
                {data.receiptType === "ipd" && <IpdReceipt data={data} />}
                {data.receiptType === "pathology" && (
                  <PathologyReceipt data={data} />
                )}
              </div>
              <div className="flex flex-wrap gap-2 justify-end mt-4 print:hidden">
                {!transactionsLoading && hasSingleTransaction && invoice && (
                  <button
                    type="button"
                    onClick={() => {
                      const tx = transactions[0];
                      setSelectedReceiptData({
                        patientName: invoice.patientName ?? "",
                        receiptNumber: (tx as any).receiptNumber ?? (tx as any).receipt_number ?? "",
                        invoiceNumber: invoice.invoiceNumber ?? "",
                        paymentDate: (tx as any).paymentDate ?? (tx as any).payment_date,
                        paymentMode: (tx as any).paymentMode ?? (tx as any).payment_mode ?? "",
                        amount: tx.amount,
                        clinicName: invoice.clinicName,
                        notes: (tx as any).notes,
                      });
                    }}
                    className="px-4 py-2 text-sm font-semibold rounded-md border text-primary border-primary hover:bg-primary/5"
                  >
                    View Receipt
                  </button>
                )}
                {!transactionsLoading && hasMultipleTransactions && (
                  <button
                    type="button"
                    onClick={() => setShowTransactionsModal(true)}
                    className="px-4 py-2 text-sm font-semibold rounded-md border text-primary border-primary hover:bg-primary/5"
                  >
                    View Transactions
                  </button>
                )}
                <button
                  type="button"
                  onClick={handlePrint}
                  disabled={receiptType === "opd" && pdfLoading}
                  className="px-4 py-2 text-sm font-semibold text-white rounded-md bg-primary hover:bg-hover disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {receiptType === "opd" && pdfLoading ? "Generating..." : "Print"}
                </button>
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 text-sm font-semibold text-gray-700 rounded-md border border-gray-300 hover:bg-gray-50"
                >
                  Close
                </button>
              </div>
            </>
          )}
        </div>
      }
    />
  );
};

export default ReceiptModal;
